import { Dispatch, SetStateAction } from 'react';
import { Divider, H5 } from '@blueprintjs/core';
import { ace_index } from '@axo/shared/data-access/types';
import { ace_index_hooks } from '@axo/shared/data-access/hooks';
import classes from './SearchFilterCard.module.css';
import { AcceptedBy } from './AcceptedBy';
import { AceCard, IAceCard } from '../AceCard';
import { ActionNeeded } from './ActionNeeded';
import { ApplicationStatusFilterSet } from './ApplicationStatusFilterSet';
import { ApprovedBy } from './ApprovedBy';
import { ClearFilterButton } from './ClearFilterButton';
import { CompletedAfter } from './CompletedAfter';
import { CreatedAfter } from './CreatedAfter';
import { CreatedBy } from './CreatedBy';
import { CreatedByTelemarketing } from './CreatedByTelemarketing';
import { FilterSet } from '../FilterSet';
import { FirstAcceptedOfferBetween } from './FirstAcceptedOfferBetween';
import { FirstApprovedOfferBetween } from './FirstApprovedOfferBetween';
import { HasCancelledOffer } from './HasCancelledOffer';
import { HasGivenMarketingConsent } from './HasGivenMarketingConsent';
import { HasGivenTransactionalConsent } from './HasGivenTransactionalConsent';
import { HasCreditWarning } from './HasCreditWarning';
import { HasPaymentRemark } from './HasPaymentRemark';
import { IconMenu } from '../IconMenu';
import { LastAcceptedOfferBetween } from './LastAcceptedOfferBetween';
import { LastCallBetweenOffset } from './LastCallBetween';
import { LastMyPageLoginBetween } from './LastMyPageLoginBetween';
import { LoanQuoteStatusFilterSet } from './LoanQuoteStatusFilterSet';
import { LoanRequestStatusFilterSet } from './LoanRequestStatusFilterSet';
import { MultipleApprovedQuotes } from './MultipleApprovedQuotes';
import { NumAnsweredCallsBetween } from './NumAnsweredCallsBetween';
import { NumCallsBetween } from './NumCallsBetween';
import { NumLoanRequestsAnsweredBetween } from './NumLoanRequestsAnsweredBetween';
import { NumLoanRequestsSentBetween } from './NumLoanRequestsSentBetween';
import { NumNotAnsweredCallsBetween } from './NumNotAnsweredCallsBetween';
import { Partner } from './Partner';
import { Portfolio } from './Portfolio';
import { Product } from './Product';
import { Purpose } from './Purpose';
import {
  ApplicationUpdatedAfter,
  ApplicationOrQuotesUpdatedAfter,
} from './UpdatedAfter';
import { PaymentRemarkStatus } from './PaymentRemarkStatus';
import { LastCompletedStep } from './LastCompletedStep';
import { HasSuccessfulAuthAttempt } from './HasSuccessfulAuthAttempt';
import { Is1POnly } from './Is1POnly';
import { Brand } from './Brand';

interface Props {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  clearQuery: () => void;
  marketCountry: string;
  cardProps: Omit<IAceCard, 'children'>;
  editable?: boolean;
}

export function SearchFilterCard({
  query,
  setQuery,
  clearQuery,
  marketCountry,
  editable = false,
  cardProps,
}: Props) {
  const active = !ace_index_hooks.useACEIndexQuery(query).isIdle;

  const filterProps = {
    query,
    setQuery,
    editable,
  };

  return (
    <AceCard {...cardProps}>
      <IconMenu>
        <ClearFilterButton
          title="filter"
          disabled={!editable || !active}
          onClick={clearQuery}
        />
      </IconMenu>

      <H5 className={classes.subHeader}>Application Status</H5>
      <ApplicationStatusFilterSet {...filterProps} />

      <Divider />

      <H5 className={classes.subHeader}>Quote Status</H5>
      <LoanQuoteStatusFilterSet {...filterProps} />

      <Divider />

      <H5 className={classes.subHeader}>Request Status</H5>
      <LoanRequestStatusFilterSet {...filterProps} />

      <Divider />

      <H5 className={classes.subHeader}>Additional filter</H5>
      <FilterSet>
        <Product {...filterProps} />
        <Portfolio {...filterProps} />
        <CreatedBy {...filterProps} />
        <MultipleApprovedQuotes {...filterProps} />
        <ApplicationUpdatedAfter {...filterProps} />
        <ApplicationOrQuotesUpdatedAfter {...filterProps} />
        <CreatedAfter {...filterProps} />
        <CompletedAfter {...filterProps} />
        <ActionNeeded {...filterProps} />
        <CreatedByTelemarketing {...filterProps} />
        <Purpose {...filterProps} />
        <AcceptedBy {...filterProps} />
        <LastAcceptedOfferBetween {...filterProps} />
        <NumCallsBetween {...filterProps} />
        <NumAnsweredCallsBetween {...filterProps} />
        <NumNotAnsweredCallsBetween {...filterProps} />
        <LastCallBetweenOffset {...filterProps} />
        <LastMyPageLoginBetween {...filterProps} />
        <HasCancelledOffer {...filterProps} />
        <ApprovedBy marketCountry={marketCountry} {...filterProps} />
        <FirstAcceptedOfferBetween {...filterProps} />
        <FirstApprovedOfferBetween {...filterProps} />
        <NumLoanRequestsSentBetween {...filterProps} />
        <NumLoanRequestsAnsweredBetween {...filterProps} />
        <HasPaymentRemark {...filterProps} />
        <PaymentRemarkStatus {...filterProps} />
        <HasCreditWarning {...filterProps} />
        <HasGivenMarketingConsent {...filterProps} />
        <HasGivenTransactionalConsent {...filterProps} />
        <HasSuccessfulAuthAttempt {...filterProps} />
        <LastCompletedStep {...filterProps} />
        <Partner {...filterProps} />
        <Is1POnly {...filterProps} />
        <Brand marketCountry={marketCountry} {...filterProps} />
      </FilterSet>
    </AceCard>
  );
}
